import { shouldUseNopBalance, shouldUseNopProfitLoss } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { setNopPositions, setNopPositionStatus } from '@/state/reducers/nopPositionSlice';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';
import { toastLogout } from '../hooks/useToast';
import { Logger } from '../logger';

export interface NopPosition {
    instrumentId: number;
    instrumentName: string;
    instrumentWithDigitalAssetName?: string;
    amount: number;
    openPrice?: number;
    realizedProfitLoss: number;
    realizedProfitLossInBaseCurrency: number;
    unrealizedProfitLoss?: number;
    unrealizedProfitLossInBaseCurrency?: number;
    valueDate: string;
}

export interface Positions {
    cryptoCfd: NopPosition[];
    commodityCfd: NopPosition[];
    fx: NopPosition[];
    indexCfd: NopPosition[];
    futureCfd: NopPosition[];
    baseCurrency: string;
    isHeartBeat: boolean;
}

export interface PositionsContext extends Partial<WebSocketHook> {}

export interface PositionsProviderProps {
    children: React.ReactNode;
}

export const errorMessages = [
    'Session token is missing',
    'Session token is invalid',
    'Access token is no longer valid'
];

export const warnMessages = ['Celer Bridge is not available', 'Temporary internal error occurred'];

const nopPositionsURL = window.config?.positions?.websocket;

const PositionsContext = createContext({} as PositionsContext);
export const usePositions = () => useContext(PositionsContext);
export const PositionsProvider = (props: PositionsProviderProps) => {
    const dispatch = useAppDispatch();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const didUnmount = useRef(false);

    const isMarginAccount = useMemo(() => {
        if (currentAccount && credentials) {
            const accountProperties = credentials.accounts.find(
                (account) => account.code === currentAccount
            )?.properties;
            return accountProperties?.find((property) => property.key === 'TYPE')?.value === 'MARGIN';
        }
    }, [currentAccount, credentials]);

    const wsURL = useMemo(() => {
        if (credentials && currentAccount && isMarginAccount && shouldUseNopBalance && shouldUseNopProfitLoss) {
            return `${nopPositionsURL}/${credentials.username}/${currentAccount}?sessionToken=${credentials.authToken}`;
        } else return null;
    }, [credentials, currentAccount]);

    const options = useMemo(
        () => ({
            reconnectAttempts: 5,
            retryOnError: true,
            reconnectInterval: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
            shouldReconnect: (event) => event.code !== 1000,
            onError: (e) => {
                if (credentials?.username && currentAccount) {
                    logToServer(
                        'warn',
                        `User [${
                            credentials.username
                        } | ${currentAccount}] encountered error on PositionsProvider WebSocket: ${
                            (e as ErrorEvent).message || e.type
                        }.`
                    );
                }
                Logger({
                    title: `PositionsProvider WebSocket Error.`,
                    callback: () => {
                        console.log(e);
                    }
                });
            },
            onClose: (e) => {
                if (credentials?.username && currentAccount) {
                    logToServer(
                        'warn',
                        `User [${
                            credentials.username
                        } | ${currentAccount}] closed PositionsProvider WebSocket — code: ${e?.code}, reason: ${
                            e?.reason || 'none'
                        }.`
                    );
                }
                Logger({
                    title: `PositionsProvider WebSocket Closed.`,
                    callback: () => {
                        console.log(e);
                    }
                });
            }
        }),
        []
    );

    const websocket = useWebSocket<Positions>(wsURL, options);

    const { readyState, lastJsonMessage, lastMessage, getWebSocket } = websocket;

    useEffect(() => {
        dispatch(setNopPositionStatus(ReadyState[readyState] as keyof typeof ReadyState));
    }, [readyState]);

    useEffect(() => {
        if (lastJsonMessage && !lastJsonMessage.isHeartBeat) {
            dispatch(setNopPositions(lastJsonMessage));
        }
    }, [lastJsonMessage]);

    useEffect(() => {
        if (errorMessages.includes(lastMessage?.data || '') && credentials) {
            Logger({
                title: `Inbound: NOP Positions Error - Message: ${lastMessage?.data || ''}.`,
                callback: () => {}
            });
            logToServer(
                'warn',
                `User [${credentials.username}] has been logged out due to NOP Positions error - Message: ${
                    lastMessage?.data || ''
                }`
            );
            toastLogout(credentials);
        } else if (warnMessages.includes(lastMessage?.data || '') && credentials) {
            Logger({
                title: `Inbound: NOP Positions Warning - Message: ${lastMessage?.data || ''}.`,
                callback: () => {}
            });
            logToServer(
                'warn',
                `User [${credentials.username}] is missing Cash Positions due to NOP Positions error - Message: ${
                    lastMessage?.data || ''
                }`
            );
        }
    }, [lastMessage, credentials]);

    const value = useMemo<PositionsContext>(() => {
        return { readyState, lastJsonMessage, getWebSocket };
    }, [readyState, lastJsonMessage, getWebSocket]);

    useEffect(() => {
        return () => {
            didUnmount.current = true;
        };
    }, []);

    return <PositionsContext.Provider value={value}>{props.children}</PositionsContext.Provider>;
};
